import React, { forwardRef, useMemo, useState } from 'react';
import { PartnerDetailsPopUp } from '../TodayStatus/PartnerDetailsPopUp';
import IconToolTip from '../../../common/IconToolTip';
import OverflowTip from '../../../common/OverflowTip';
import { ScogoID, validateStringValue } from '../../../utils/common';
import { Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import PMDetails from '../../project/Modals/PmDetails';
import { openCustomModal } from '../../../actions/modal';
import { canBulkRemoveCluster } from '../../../utils/access';
import RemoveClusterAndPspModal from '../Modal/RemoveClusterAndPsp';
import { TicketAgentRole, MilestoneType } from '../TicketController';

export default function Contact({ ticket, dataToShowPerRow = 1, allowRemoveClusterAndPsp = false }) {
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    // Same permission that gates the Bulk Remove Cluster & PSP action.
    const canRemoveClusterAndPsp = allowRemoveClusterAndPsp && canBulkRemoveCluster(newLoggedUserData?.actions);

    const memoizedAgentContact = useMemo(() => {
        let owners = [];
        let pms = [];
        let agents = [];

        // A Warehouse Owner is only relevant to RMA milestones. Ignore any WO whose milestone_id points to a
        // non-RMA milestone. (Only applied when milestone data is available, otherwise we can't determine the type.)
        const rmaMilestoneIds = new Set((ticket?.milestones || []).filter((m) => m.type === MilestoneType.RMA).map((m) => m.id));
        const hasMilestones = Array.isArray(ticket?.milestones) && ticket.milestones.length > 0;

        ticket.agents.map(({ role, agent, milestone_id }) => {
            if (role === TicketAgentRole.WAREHOUSE_OWNER && hasMilestones && milestone_id != null && !rmaMilestoneIds.has(milestone_id)) {
                return;
            }
            if ([TicketAgentRole.CLUSTER, TicketAgentRole.PSP, TicketAgentRole.SUPERVISOR, TicketAgentRole.WAREHOUSE_OWNER].includes(role)) {
                owners.push({
                    name: `${validateStringValue(agent.display_name)}`,
                    id: agent.id,
                    role,
                    label: role === TicketAgentRole.WAREHOUSE_OWNER ? 'WO' : role === TicketAgentRole.CLUSTER ? 'CL' : role === TicketAgentRole.PSP ? 'PSP' : role === TicketAgentRole.SUPERVISOR ? 'SV' : ''
                })
            } else if ([TicketAgentRole.PM, TicketAgentRole.PM1, TicketAgentRole.PM2, TicketAgentRole.PM3, TicketAgentRole.EM].includes(role)) {
                pms.push({
                    name: `${validateStringValue(agent.display_name)}`,
                    id: agent.id,
                    role,
                })
            } else if ([TicketAgentRole.REMOTE_SUPPORT].includes(role)) {
                agents.push({
                    name: `${validateStringValue(agent.display_name)}`,
                    id: agent.id,
                    role,
                })
            }
        })

        pms = Array.from(new Map(pms.map(item => [item.id, item])).values())
        pms.sort((a, b) => {
            const rolePriority = {
                [TicketAgentRole.PM1]: 1,
                [TicketAgentRole.PM2]: 2,
                [TicketAgentRole.PM3]: 3,
                [TicketAgentRole.EM]: 4,
                [TicketAgentRole.PM]: 5
            };
            return (rolePriority[a.role]) - (rolePriority[b.role]);
        })
        return { owners: Array.from(new Map(owners.map(item => [item.role, item])).values()), pms: pms, agents: Array.from(new Map(agents.map(item => [item.id, item])).values()) };
    }, [ticket?.agents, ticket?.milestones]);

    const openPmClick = () => {
        if (!ticket.fk_project_id) return;
        const pmList = [{
            name: `${validateStringValue(ticket?.pm_first_name)} ${validateStringValue(ticket?.pm_last_name)}`, mobile: ticket?.pm_mobile,
            label: 'Project Manager'
        }];
        dispatch(openCustomModal({
            modalComponent: <PMDetails projectId={ticket.fk_project_id} pmList={pmList} />,
            modalWidth: '40rem',
            modalHeight: 'auto',
            heading: "Project Managers",
        }))
    }

    let showPmContact = ticket.helpdesk_id === ScogoID;

    return <div className='w-full'>

        {
            memoizedAgentContact.owners.map(({ id, name, label, role }) => {
                // The remove hangs off the cluster row because it takes the cluster and the PSP off together.
                const action = canRemoveClusterAndPsp && role === TicketAgentRole.CLUSTER ? <RemoveClusterAndPspIcon ticket={ticket} /> : null;
                return <ContactDetail key={id} id={id} name={name} label={label} action={action} />
            })
        }

        {showPmContact && memoizedAgentContact.pms.length > 0 && <div className='w-[10rem]'><ContactDetail labelClassName='cursor-pointer hover:text-scogoorange text-scogogray max-w-max text-font12' onLabelClick={openPmClick} id={memoizedAgentContact.pms[0].id} name={`${memoizedAgentContact.pms[0].name}`} label={`PM`} /></div>}
        <div className='flex items-center gap-x-1 w-full'>
            {Array.isArray(memoizedAgentContact.agents) && memoizedAgentContact.agents.length > 0 &&
                memoizedAgentContact.agents?.slice(0, dataToShowPerRow)?.map((agent) => {
                    return (
                        <div key={agent.id} className='inline-flex gap-2 truncate w-full'>
                            <span className='text-scogogray max-w-max text-font12' style={{ maxWidth: '40%' }}>Agent: </span>
                            <span className='max-w-max truncate' style={{ maxWidth: '10rem' }}>
                                <OverflowTip textClass='truncate text-scogobgsky cursor-pointer text-left text-font11 hover:text-scogoorange w-full' someLongText={`${validateStringValue(agent.name)}`} />
                            </span>
                        </div>
                    );
                })}

            {Array.isArray(memoizedAgentContact.agents) && memoizedAgentContact.agents.length > dataToShowPerRow && (
                <>
                    <AgentPopup agents={memoizedAgentContact.agents?.slice(dataToShowPerRow)} anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }} >
                        <span className='text-font12 round font-normal text-scogoorange relative cursor-pointer hover:text-scogobgsky countOfBOQ'>
                            +{memoizedAgentContact.agents.length - dataToShowPerRow}
                        </span>
                    </AgentPopup>
                </>
            )}
        </div>
    </div>
}



const ContactDetail = ({ id, name, label, onLabelClick = () => { }, labelClassName = 'text-scogogray max-w-max text-font12', action = null }) => {
    return <div className='inline-flex items-center gap-2 truncate w-full'>
        <span onClick={onLabelClick} className={labelClassName} style={{ maxWidth: 'max-content' }}>{label}: </span>
        <span className='max-w-max truncate' style={{ maxWidth: '70%' }}>
            <PartnerDetailsPopUp userId={id} displayName={name} />
        </span>
        {action}
    </div>
}

// Same endpoint as the Bulk Remove Cluster & PSP action, scoped to this one ticket.
const openRemoveClusterAndPspModal = ({ ticket, dispatch }) => {
    const heading = (
        <span>
            Remove Cluster / PSP :&nbsp;<span className='text-scogobgsky text-font16'>{validateStringValue(ticket.ticket_number)}</span>
        </span>
    );
    dispatch(openCustomModal({ modalComponent: <RemoveClusterAndPspModal ticketIds={[ticket.id]} />, heading, modalWidth: '45rem', modalHeight: 'auto' }));
};

const RemoveClusterAndPspIcon = ({ ticket }) => {
    const dispatch = useDispatch();
    return (
        <IconToolTip title='Remove Cluster / PSP' placement='bottom'>
            <span
                className='material-icons text-font14 text-scogoclosed cursor-pointer hover:text-scogoorange'
                onClick={() => openRemoveClusterAndPspModal({ ticket, dispatch })}
            >
                delete
            </span>
        </IconToolTip>
    );
};


const Popup = forwardRef(({ agents, close }, ref) => {
    return (
        <div className='bg-white max-h-44 cursor-pointer max-w-56 p-2  z-100 border shadow-lg boqpop'>
            {agents?.map((agent) => {
                return (
                    <IconToolTip title={`${validateStringValue(agent.name)}`}>
                        <span className={`bg-scogoorange text-white max-w-12  w-fit text-font10 px-2 flex items-center rounded-3px  mb-1`} >
                            {`${validateStringValue(agent.name)}`}
                        </span>
                    </IconToolTip>
                );
            })}
        </div>
    );
});

const AgentPopup = ({
    children,
    agents,
    anchorOrigin = {
        vertical: 'top',
        horizontal: 'center',
    },
    transformOrigin = {
        vertical: 'bottom',
        horizontal: 'center',
    },
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
        <>
            <div aria-describedby={id} onClick={handleClick} variant='contained' className=' hover:underline cursor-pointer inline'>
                {children}
            </div>
            <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={anchorOrigin} transformOrigin={transformOrigin}>
                <Popup agents={agents} close={handleClose} />
            </Popover>
        </>
    );
};
